import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { toast } from '@app/components/ui/Toast/toast'
import { Button } from '@src/components/ui/button'
import { FunnelIcon } from 'lucide-react'
import { useState } from 'react'
import { ALL_DAMAGE_STATE_VALUES, DAMAGE_STATE_OPTIONS } from '../constants/riskProfile.constants'
import type { DamageState } from '../types/riskProfileSelected.types'

export interface RiskProfileDamageStateFilterProps {
  damageStates: DamageState[]
  onApply: (damageStates: DamageState[]) => void
}

export function RiskProfileDamageStateFilter({
  damageStates,
  onApply,
}: RiskProfileDamageStateFilterProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<DamageState[]>(
    damageStates.length === 0 ? ALL_DAMAGE_STATE_VALUES : damageStates,
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(damageStates.length === 0 ? ALL_DAMAGE_STATE_VALUES : damageStates)
    }
    setOpen(nextOpen)
  }

  const toggle = (value: DamageState) => {
    setDraft((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    )
  }

  const handleApply = () => {
    if (draft.length === 0) {
      return toast({
        type: 'error',
        description: 'You need at least one damage state selected',
        position: 'top-center',
      })
    }

    onApply(draft)
    setOpen(false)
  }

  const handleClearAll = () => setDraft([])
  const handleSelectAll = () => setDraft(ALL_DAMAGE_STATE_VALUES)

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" className="gap-2 bg-transparent">
          <FunnelIcon className="h-4 w-4" />
          Filters{' '}
          {/* if all selected show nothing, if none selected show nothing, if some selected show count */}
          {damageStates.length > 0 && damageStates.length < ALL_DAMAGE_STATE_VALUES.length ? (
            <div className="bg-rexTeal-500 h-4 w-4 rounded-full text-xs font-semibold text-white mx-auto my-auto">
              {damageStates.length}
            </div>
          ) : (
            ''
          )}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
          <Separator orientation="horizontal" />
        </SheetHeader>

        <div className="py-4">
          <h3 className="mb-3 text-sm font-semibold">Risk</h3>
          <div className="flex flex-col gap-3">
            {DAMAGE_STATE_OPTIONS.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.includes(option.value)}
                  onCheckedChange={() => toggle(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        <SheetFooter>
          {draft.length === 0 ? (
            <Button type="button" variant="outline" onClick={handleSelectAll}>
              Select all
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={handleClearAll}>
              Clear all
            </Button>
          )}
          <Button type="button" onClick={handleApply}>
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
